import { Box, Collapse, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';

import PaymentInvoiceAccordionContent from '../../../components/tableComponent/PaymentInvoiceAccordionContent';
import { colors } from '../../../components/theme/theme';
import { PaymentInvoiceDetail } from '../../../services/payment-services';
import { useTranslation } from '../../../utils/translations';

interface PaymentHistoryCardProps {
    row: Record<string, unknown>;
}

/**
 * Mobile/tablet-only card rendering of a single payment-history row for the global payment
 * history screen (pages/payments/payment-history.tsx): collapsed summary + expandable itemized
 * invoice breakdown, matching the approved mobile design. Desktop keeps using CommonTable.
 */
export const PaymentHistoryCard: React.FC<PaymentHistoryCardProps> = ({ row }) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const invoices = (row.invoices as PaymentInvoiceDetail[]) || [];

    return (
        <Box
            sx={{
                border: `1px solid ${colors.greyPreset[30]}`,
                borderRadius: 1.5,
                backgroundColor: colors.fullWhite,
                overflow: 'hidden',
            }}
        >
            <Box
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                sx={{ p: 1.5, cursor: 'pointer' }}
                onClick={() => setExpanded(prev => !prev)}
                onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setExpanded(prev => !prev);
                    }
                }}
            >
                <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                    <Stack direction="row" gap={0.75} alignItems="center">
                        <span
                            className="icon-arrow-drop-down"
                            style={{
                                fontSize: 20,
                                color: colors.greyPreset[100],
                                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s ease',
                                display: 'inline-block',
                            }}
                        />
                        <Typography variant="body2" color="textSecondary">
                            {t('payments.paymentHistory.contract') || 'Contract'}:
                        </Typography>
                        <Typography variant="subtitle2" fontWeight={700}>
                            {String(row.contractId || 'N/A')}
                        </Typography>
                    </Stack>
                    <Stack direction="row" gap={0.5} alignItems="baseline" flexShrink={0}>
                        <Typography variant="caption" color="textSecondary">
                            {(t('payments.paymentHistory.amount') || 'Amount').toUpperCase()}:
                        </Typography>
                        <Typography variant="subtitle1" fontWeight={700}>
                            {String(row.amount || '')}
                        </Typography>
                    </Stack>
                </Stack>
                <Box
                    sx={{
                        backgroundColor: colors.greyPreset[90],
                        borderRadius: 1,
                        p: 1.5,
                        mt: 1.5,
                    }}
                >
                    <Stack direction="row" justifyContent="space-between" gap={1}>
                        <Stack spacing={0.25}>
                            <Typography variant="caption" color="textSecondary">
                                {t('contracts.paymentHistory.paymentEffectiveDate') || 'Payment Effective Date'}
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                                {String(row.paymentDate || 'N/A')}
                            </Typography>
                        </Stack>
                        <Stack spacing={0.25}>
                            <Typography variant="caption" color="textSecondary">
                                {t('contracts.paymentHistory.referenceNumber') || 'Reference Number'}
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                                {String(row.referenceNumber || 'N/A')}
                            </Typography>
                        </Stack>
                        <Stack spacing={0.25} alignItems="flex-end">
                            <Typography variant="caption" color="textSecondary">
                                {t('contracts.paymentHistory.method') || 'Method'}
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                                {String(row.paymentType || 'N/A')}
                            </Typography>
                        </Stack>
                    </Stack>
                </Box>
            </Box>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <PaymentInvoiceAccordionContent invoices={invoices} withTitleTooltips />
            </Collapse>
        </Box>
    );
};
